import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type Section } from "~/api/types";
import { Badge } from "~/atoms/badge";
import { getDraftDocumentsNotUsingLatestSection } from "~/helpers/sections";

export const OutdatedVersionsBadge = () => {
  const { t } = useTranslation();

  return (
    <Badge
      variant="outline"
      className="flex gap-1 border-orange-500/50 bg-orange-500/10 text-orange-700"
    >
      <AlertTriangle className="size-3" />
      {t("commons.sections.outdatedVersionsWarning")}
    </Badge>
  );
};

interface SectionsContainerProps {
  sections: Section[];
  title: string;
  selectedSectionId?: number;
  onSectionClick: (section: Section) => void;
  usedSection: boolean;
}

const SectionsContainer = ({
  sections,
  title,
  selectedSectionId,
  onSectionClick,
  usedSection,
}: SectionsContainerProps) => {
  const { t } = useTranslation();

  const hasOutdatedVersionsInUse = (section: Section) => {
    return getDraftDocumentsNotUsingLatestSection(section) !== null;
  };

  const calculateNumberOfUsages = (section: Section) => {
    let usages = 0;
    section.sectionVersions.forEach((sv) => {
      usages = usages + sv.usedInDocumentVersions.length;
    });

    return usages;
  };

  return (
    <div className="flex min-h-0 flex-auto flex-col gap-2">
      <p className="text-sm font-medium">{title}</p>
      <div className="bg-background border-item-border min-h-0 basis-auto overflow-auto rounded-2xl border">
        {sections.map((section) => (
          <div
            key={section.id}
            className={`border-item-border hover:bg-selected/50 flex border-b p-4 hover:cursor-pointer ${selectedSectionId && section.id === selectedSectionId && "bg-selected"}`}
            onClick={() => onSectionClick(section)}
          >
            <div className="flex w-full items-start justify-between gap-2">
              <div className="flex w-xs min-w-0 flex-1 flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between">
                  <p className="text-sm">{section.name}</p>
                  {usedSection && hasOutdatedVersionsInUse(section) && (
                    <OutdatedVersionsBadge />
                  )}
                </div>
                <div className="flex justify-between">
                  <p className="text-muted-foreground text-xs">
                    {t("commons.sections.versionsCount", {
                      count: section.sectionVersions.length,
                    })}
                  </p>
                  {usedSection ? (
                    <p className="text-muted-foreground text-xs">
                      {t("commons.sections.usedInDocumentsCount", {
                        count: calculateNumberOfUsages(section),
                      })}
                    </p>
                  ) : (
                    <p className="text-muted-foreground text-xs italic">
                      {t("commons.sections.noDocuments")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface SectionsPanelProps {
  sections: Section[];
  onSectionClick: (section: Section) => void;
  selectedSection: Section | null;
}

export const SectionsPanel = ({
  sections,
  onSectionClick,
  selectedSection,
}: SectionsPanelProps) => {
  const { t } = useTranslation();
  const sectionHasUsage = (section: Section) => {
    return section.sectionVersions.some(
      (sv) => sv.usedInDocumentVersions.length > 0,
    );
  };

  sections.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });

  const usedSections: Section[] = [];
  const unUsedSections: Section[] = [];

  sections.forEach((s) => {
    if (sectionHasUsage(s)) {
      usedSections.push(s);
    } else {
      unUsedSections.push(s);
    }
  });

  return (
    <div className="flex h-full flex-col gap-10">
      {usedSections.length > 0 && (
        <SectionsContainer
          sections={usedSections}
          title={t("commons.sections.inDocuments")}
          selectedSectionId={selectedSection?.id}
          onSectionClick={onSectionClick}
          usedSection={true}
        />
      )}

      {unUsedSections.length > 0 && (
        <SectionsContainer
          sections={unUsedSections}
          title={t("commons.sections.unused")}
          selectedSectionId={selectedSection?.id}
          onSectionClick={onSectionClick}
          usedSection={false}
        />
      )}
    </div>
  );
};
